// NOSONAR
import EntityNameEditButton from '@/components/onboarding_components/EntityNameEditButton/EntityNameEditButton.vue';
import { myQueueMethods } from './my_queue_methods';
import {
  fetch_and_open_agreement_document,
  get_agreement_submission_id,
  prepare_agreement_download_state,
  resolve_agreement_download_error,
  set_missing_agreement_submission_error
} from './my_queue_agreement_helpers';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';
import {
  resolve_onboarding_journey_filter,
  resolve_onboarding_journey_filter_key
} from '@/services/onboarding_journey_filter_service';

export default {
  name: 'OnboardingMyQueue',
  components: {
    EntityNameEditButton
  },
  data: () => ({
    is_loading_queue: false,
    is_filtering_queue: false,
    has_loaded_queue_once: false,
    is_loading_detail: false,
    queue_error: '',
    queue_search: '',
    queue_stage_filter: '',
    queue_risk_filter: '',
    queue_action_key: '',
    queue_items: [],
    selected_case: null,
    active_detail_tab: 'summary',
    detail_tabs: [
      { id: 'summary', label: 'Summary', icon: 'bi-grid' }
    ],
    detail_tab_overflow: {
      left: false,
      right: false
    },
    summary_rows: [],
    compliance_panel: null,
    compliance_action_key: '',
    compliance_error: '',
    profile_rows: [],
    contact_rows: [],
    stage_timeline: [],
    evidence_items: [],
    case_form_sections: [],
    agreement_rows: [],
    agreement_download_key: '',
    show_edit_modal: false,
    available_owner_roles: [],
    available_edit_journeys: [],
    available_edit_templates: [],
    edit_template_search: '',
    edit_owner_role_search: '',
    show_edit_template_dropdown: false,
    show_edit_owner_dropdown: false,
    edit_error: '',
    editing_application_id: null,
    is_saving_case_edits: false,
    edit_form: {
      candidate_name: '',
      journey: '',
      template_name: '',
      template_ids: [],
      onboarding_name: '',
      owner_role_id: '',
      owner_role_ids: [],
      owner_role_name: '',
      activate_by: '',
      email: '',
      allow_login: false
    },
    suppress_next_onboarding_data_update: false,
    ...tablePaginationState()
  }),
  computed: {
    active_status_filter() {
      return String(this.$route.query?.status || '').trim().toLowerCase();
    },
    active_journey_filter() {
      return resolve_onboarding_journey_filter_key(this.$route);
    },
    active_journey_label() {
      return resolve_onboarding_journey_filter(this.$route).label || 'All Journeys';
    },
    filtered_queue_items() {
      const normalized_status_filter = this.active_status_filter;
      const normalized_journey_filter = this.active_journey_filter;
      const status_filtered_items = normalized_status_filter
        ? this.queue_items.filter((item) => {
          const candidate_values = [
            item?.gate,
            item?.case_item?.workflow_display_stage,
            item?.case_item?.current_workflow_status,
            item?.case_item?.workflow_status_name,
            item?.case_item?.workflow_status,
            item?.case_item?.stage,
            item?.case_item?.detail,
            item?.case_item?.activation,
            item?.case_item?.application_status,
            item?.case_item?.status?.status,
            item?.case_item?.status?.name,
            item?.case_item?.status_name,
            item?.case_item?.status
          ];

          return candidate_values.some((value) => (
            String(value || '').trim().toLowerCase() === normalized_status_filter
          ));
        })
        : this.queue_items;
      const journey_filtered_items = normalized_journey_filter
        ? status_filtered_items.filter((item) => {
          const candidate_values = [
            item?.case_item?.entity_type_id,
            item?.case_item?.threshold_entity_type_id_used,
            item?.case_item?.journey_tone,
            item?.case_item?.journey_key,
            item?.journey,
            item?.case_item?.journey
          ];

          return candidate_values.some((value) => (
            String(value || '').trim().toLowerCase() === normalized_journey_filter
          ));
        })
        : status_filtered_items;
      const filtered_items = journey_filtered_items.filter((item) => (
        this.queue_item_matches_table_filters(item)
      ));

      return filtered_items;
    },
    queue_stage_filter_options() {
      return this.unique_queue_values((item) => this.queue_status_label(item));
    },
    queue_risk_filter_options() {
      return this.unique_queue_values((item) => item.priority);
    },
    item_count() {
      return this.filtered_queue_items.length;
    },
    queue_loading_label() {
      return this.is_filtering_queue ? 'Loading gate...' : 'Loading onboardings...';
    },
    table_total_rows() {
      return this.filtered_queue_items.length;
    },
    paginated_queue_items() {
      return this.paginate_table_rows(this.filtered_queue_items);
    },
    ...tablePaginationComputed,
    total_item_count() {
      return this.queue_items.length;
    },
    active_form_section_tab() {
      return this.detail_tabs.find((tab) => (
        tab.id === this.active_detail_tab
        && !['summary', 'agreements'].includes(tab.id)
      )) || null;
    },
    active_form_section_is_document_section() {
      const section_label = String(this.active_form_section_tab?.section?.label || '').trim().toLowerCase();
      const form_name = String(this.active_form_section_tab?.form_name || '').trim().toLowerCase();

      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => (
        section_label.includes(word) || form_name.includes(word)
      ));
    }
  },
  mounted() {
    window.dispatchEvent(new CustomEvent('onboarding-case-scope-cleared'));
    this.load_queue_items();
    window.addEventListener('onboarding-case-created', this.handle_created_onboarding);
    window.addEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.addEventListener('resize', this.update_detail_tab_overflow);
  },
  beforeUnmount() {
    window.removeEventListener('onboarding-case-created', this.handle_created_onboarding);
    window.removeEventListener('onboarding-data-updated', this.handle_onboarding_data_updated);
    window.removeEventListener('resize', this.update_detail_tab_overflow);
  },
  activated() {
    if (!this.has_loaded_queue_once) return;
    window.dispatchEvent(new CustomEvent('onboarding-case-scope-cleared'));
    this.queue_action_key = '';
    this.load_queue_items();
  },
  watch: {
    queue_search() {
      this.reset_table_page();
    },
    queue_stage_filter() {
      this.reset_table_page();
    },
    queue_risk_filter() {
      this.reset_table_page();
    },
    active_status_filter() {
      this.handle_route_filter_change();
    },
    active_journey_filter() {
      this.handle_route_filter_change();
    }
  },
  methods: {
    handle_route_filter_change() {
      this.is_filtering_queue = true;
      window.dispatchEvent(new CustomEvent('system-loader:show', {
        detail: this.queue_loading_label
      }));
      this.reset_table_page();
      this.$nextTick(() => {
        this.is_filtering_queue = false;
        window.dispatchEvent(new CustomEvent('system-loader:hide'));
      });
    },
    queue_item_matches_table_filters(item) {
      return this.queue_item_matches_stage(item)
        && this.queue_item_matches_risk(item)
        && this.queue_item_matches_search(item);
    },
    normalize_queue_filter_value(value) {
      return String(value || '').trim().toLowerCase();
    },
    queue_item_matches_stage(item) {
      const normalized_stage = this.normalize_queue_filter_value(this.queue_stage_filter);
      const stage_label = this.normalize_queue_filter_value(this.queue_status_label(item));

      return !normalized_stage || stage_label === normalized_stage;
    },
    queue_item_matches_risk(item) {
      const normalized_risk = this.normalize_queue_filter_value(this.queue_risk_filter);
      const risk_label = this.normalize_queue_filter_value(item?.priority);

      return !normalized_risk || risk_label === normalized_risk;
    },
    queue_item_matches_search(item) {
      const search = this.normalize_queue_filter_value(this.queue_search);
      const candidate_values = [
        item?.title,
        item?.company,
        item?.journey,
        item?.gate,
        item?.progress,
        item?.priority,
        item?.due,
        this.queue_status_label(item),
        item?.case_item?.name,
        item?.case_item?.stage,
        item?.case_item?.owner
      ];

      return !search || candidate_values.some((value) => (
        this.normalize_queue_filter_value(value).includes(search)
      ));
    },
    unique_queue_values(mapper) {
      const values = this.queue_items
        .map((item) => String(mapper(item) || '').trim())
        .filter((value) => value);
      const unique_values = [...new Set(values)].sort((left, right) => left.localeCompare(right));

      return unique_values;
    },
    ...myQueueMethods,
    ...tablePaginationMethods,
    async download_agreement_document(agreement = {}) {
      const submission_id = get_agreement_submission_id(agreement);
      if (!submission_id) {
        set_missing_agreement_submission_error(this);
        return;
      }

      prepare_agreement_download_state(this, agreement, submission_id);

      try {
        await fetch_and_open_agreement_document(this, submission_id);
      } catch (error) {
        this.queue_error = resolve_agreement_download_error(error);
      } finally {
        this.agreement_download_key = '';
      }
    }
  }
};
